import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Spin, message } from "antd";
import { CheckCircleFilled, CreditCardOutlined, ShopOutlined } from "@ant-design/icons";

import HelcimForm from "../../Login/component/HelcimForm";
import { controller } from "../../Login/controller";
import { loadOfficeFeatures, useOfficeFeatures } from "../officeFeatures";
import PlanPicker, { PURPLE } from "./PlanPicker";
import PaymentProfileForm from "./PaymentProfileForm";
import { billingFromProfile } from "./billingAddress";
import {
  PENDING_SUBSCRIBE_KEY,
  cartLines,
  cartTotals,
  checkoutItems,
  checkoutOutcome,
  officesNeedingPlan,
  pendingPurchasePayload,
} from "./checkoutCart";
import { clearHelcimAttachMarker } from "./helcimReturn";
import { money } from "./money";
import { taxLineLabel } from "./taxLines";
import { needsPaymentProfile, subscribeErrorMessage } from "./subscribeErrors";
import { pollCheckout } from "./useHelcimReturn";
import "./checkout.css";

/**
 * Choose a plan for each office, and pay once.
 *
 * One component for the three places a practice buys: onboarding, the
 * Choose-a-plan modal and the Billing page. Each office gets its own plan (and
 * its own charge and receipt); the customer enters a card once, or uses the card
 * they already pay with ("Pay with saved card", charged right away).
 *
 * ⚠️ A new card leaves the page: Helcim's form posts and the browser comes back
 * with the card token. ``pendingSubscribe`` (v2: every office in the checkout)
 * is what lets ``useHelcimReturn`` finish the purchase wherever the user lands.
 *
 * Staff who can't buy (not a practice admin) see the plans read-only. A super
 * admin gets the owner's whole experience as a preview — every office still
 * needing a plan, offers, the order summary — with the pay buttons switched off:
 * we never pay for a practice (the server refuses a super admin's checkout).
 */
export default function MultiOfficeCheckout({ officeId, onDone, onCancel, cancelText = "Not now" }) {
  const [plans, setPlans] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [activeOffice, setActiveOffice] = useState(null);
  const [selections, setSelections] = useState({}); // officeId -> variant
  const [step, setStep] = useState("choose"); // choose | billing | card | charging | result
  const [submitting, setSubmitting] = useState(false);
  const [billing, setBilling] = useState(null);
  const [pendingPayWith, setPendingPayWith] = useState("new_card");
  const [result, setResult] = useState(null);
  const preview = !!useOfficeFeatures(officeId ? String(officeId) : undefined).isSuperAdmin;

  const load = useCallback(async () => {
    setLoadError(false);
    const res = await controller.getPracticePlans(officeId);
    if (!res || res.status >= 250) {
      setLoadError(true);
      return;
    }
    setPlans(res);
  }, [officeId]);

  useEffect(() => {
    load();
  }, [load]);

  const needing = useMemo(() => officesNeedingPlan(plans, { preview }), [plans, preview]);
  const covered = useMemo(
    () => ((plans && plans.offices) || []).filter((o) => o.plan && o.plan.status === "active"),
    [plans],
  );
  const canBuy = needing.length > 0;
  const lines = useMemo(() => cartLines(needing, selections), [needing, selections]);
  const totals = useMemo(() => cartTotals(lines), [lines]);
  const names = useMemo(() => {
    const out = {};
    ((plans && plans.offices) || []).forEach((o) => { out[String(o.office_id)] = o.office_name; });
    return out;
  }, [plans]);

  const choose = (office, tierId, variant) => {
    setSelections((prev) => ({ ...prev, [office]: variant }));
    // Move on to the next office still without a plan, so a practice with
    // several offices can go straight down the list.
    const next = needing.find((o) => o.office_id !== office && !selections[o.office_id]);
    if (next && needing.length > 1) setTimeout(() => setActiveOffice(next.office_id), 250);
  };

  const refreshGates = (ids) => {
    ids.forEach((id) => loadOfficeFeatures(String(id), { force: true }));
  };

  const start = async (payWith) => {
    if (!lines.length || preview) return;
    setSubmitting(true);
    try {
      const res = await controller.startCheckout(checkoutItems(lines), payWith);
      if (needsPaymentProfile(res)) {
        setPendingPayWith(payWith);
        setStep("billing");
        return;
      }
      if (!res || res.status >= 250) {
        message.error(subscribeErrorMessage(res));
        return;
      }
      const officeIds = lines.map((l) => String(l.officeId));
      if (payWith === "saved_card") {
        setStep("charging");
        const states = await pollCheckout(officeIds);
        const outcome = checkoutOutcome(states || {}, names);
        setResult({ states: states || {}, outcome });
        setStep("result");
        refreshGates(officeIds);
        return;
      }
      // A new card: survive Helcim's page round trip. A new checkout starts with
      // no attach history — Helcim hands back the SAME card token for a
      // returning customer, and a stale marker would mute this purchase.
      clearHelcimAttachMarker();
      localStorage.setItem(
        PENDING_SUBSCRIBE_KEY,
        pendingPurchasePayload({
          checkoutRef: res.checkout_ref,
          officeIds,
          tierIds: lines.map((l) => String(l.tierId)),
        }),
      );
      if (!billing) {
        try {
          const profile = await controller.checkUserStatus();
          setBilling(billingFromProfile(profile && profile.billing));
        } catch (e) {
          // The card form asks for the fields, as it always could.
        }
      }
      setStep("card");
    } catch (e) {
      console.error(e);
      message.error("Could not start the checkout. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Save the billing details, then pick the checkout back up where it left off.
  const handleProfile = async (values) => {
    setSubmitting(true);
    try {
      const created = await controller.createPlatformCustomer(values);
      if (!created || created.status >= 250) {
        message.error(
          (created && (created.detail || created.error)) ||
            "Could not save your billing details. Please check them and try again.",
        );
        return;
      }
      setBilling(values);
      setStep("choose");
    } catch (e) {
      console.error(e);
      message.error("Could not save your billing details. Please try again.");
      return;
    } finally {
      setSubmitting(false);
    }
    await start(pendingPayWith);
  };

  if (loadError) {
    return (
      <div className="moc-empty">
        <p>Couldn't load your offices' plans.</p>
        <Button onClick={load}>Try again</Button>
      </div>
    );
  }
  if (!plans) return <div className="moc-empty"><Spin /></div>;

  // Staff who can't buy: the plans, read-only, and who to ask.
  if (!canBuy) {
    const allCovered = covered.length && covered.length === (plans.offices || []).length;
    return (
      <div className="moc moc-readonly">
        {allCovered ? (
          <Alert type="success" showIcon message="Every office you manage already has a plan." />
        ) : (
          <Alert
            type="info"
            showIcon
            message="Ask your practice admin to choose a plan"
            description="Only a practice admin can choose and pay for your office's plan. Here's what's available."
            style={{ marginBottom: 18 }}
          />
        )}
        {allCovered ? null : <PlanPicker officeId={officeId} readOnly compact />}
        {onCancel ? (
          <div className="moc-cancel"><span onClick={onCancel}>{cancelText}</span></div>
        ) : null}
      </div>
    );
  }

  if (step === "billing") {
    return (
      <div className="co moc-narrow">
        <CartSummary lines={lines} totals={totals} compact />
        <div className="co-steps" style={{ marginTop: 14 }}>
          <div className="co-step is-current">1 · Billing details</div>
          <div className="co-step">2 · Payment</div>
        </div>
        <PaymentProfileForm onSubmit={handleProfile} submitting={submitting} />
        <span className="co-back" onClick={() => setStep("choose")}>← Back to plans</span>
      </div>
    );
  }

  if (step === "card") {
    return (
      <div className="co moc-narrow">
        <CartSummary lines={lines} totals={totals} compact />
        <div className="co-steps" style={{ marginTop: 14 }}>
          <div className="co-step is-done">1 · Billing details</div>
          <div className="co-step is-current">2 · Payment</div>
        </div>
        <HelcimForm
          billing={billing}
          chargeNote={<TotalNote totals={totals} count={lines.length} />}
          submitText={lines.length > 1 ? `Pay $${money(totals.total)} for ${lines.length} offices` : "Start subscription"}
        />
        <span className="co-back" onClick={() => setStep("choose")}>← Back to plans</span>
      </div>
    );
  }

  if (step === "charging") {
    return (
      <div className="moc-empty">
        <Spin />
        <p style={{ marginTop: 14 }}>Charging your saved card…</p>
      </div>
    );
  }

  if (step === "result" && result) {
    return (
      <div className="moc moc-narrow">
        <Alert
          type={result.outcome.kind === "success" ? "success" : result.outcome.kind === "error" ? "error" : "info"}
          showIcon
          message={result.outcome.text}
        />
        <div className="moc-results">
          {lines.map((l) => {
            const state = result.states[String(l.officeId)];
            return (
              <div key={l.officeId} className="moc-result-row">
                <span><ShopOutlined /> {l.officeName}</span>
                <span className={`moc-state moc-state-${state}`}>{STATE_TEXT[state] || "Confirming"}</span>
              </div>
            );
          })}
        </div>
        <div className="moc-actions">
          {result.outcome.kind === "error" ? (
            <Button onClick={() => { setResult(null); setStep("choose"); load(); }}>Try again</Button>
          ) : null}
          <Button type="primary" style={{ background: PURPLE, borderColor: PURPLE }}
                  onClick={() => (onDone ? onDone(result) : window.location.reload())}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  // The office picked, else the one this was opened for, else the first.
  const active = needing.find((o) => o.office_id === activeOffice)
    || needing.find((o) => String(o.office_id) === String(officeId)) || needing[0];

  return (
    <div className="moc">
      <div className="moc-main">
        {preview ? (
          <Alert
            type="info"
            showIcon
            className="moc-preview"
            message="Preview — this is what the practice's owner or admin sees"
            description="Choose plans and see the prices, offers and totals exactly as they would. Super admins can't pay for a practice, so the payment buttons are off."
          />
        ) : null}
        {needing.length > 1 ? (
          <div className="moc-offices" role="tablist">
            {needing.map((o) => {
              const chosen = selections[o.office_id];
              return (
                <button
                  key={o.office_id}
                  type="button"
                  role="tab"
                  aria-selected={o.office_id === active.office_id}
                  className={`moc-office${o.office_id === active.office_id ? " is-active" : ""}`}
                  onClick={() => setActiveOffice(o.office_id)}
                >
                  <span className="moc-office-name">
                    {chosen ? <CheckCircleFilled className="moc-office-check" /> : <ShopOutlined />}
                    {o.office_name}
                  </span>
                  <span className="moc-office-sub">
                    {chosen ? chosen.title : STATE_LABEL[o.state] || "Needs a plan"}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}
        <div className="moc-picker-head">
          <span>
            {needing.length > 1 ? "Plan for " : "Choose a plan for "}<b>{active.office_name}</b>
          </span>
          {selections[active.office_id] ? (
            <button
              type="button"
              className="moc-clear"
              onClick={() => setSelections((prev) => {
                const next = { ...prev };
                delete next[active.office_id];
                return next;
              })}
            >
              Remove
            </button>
          ) : null}
        </div>
        <PlanPicker
          key={active.office_id}
          officeId={active.office_id}
          compact
          value={selections[active.office_id] ? selections[active.office_id].id : null}
          onChange={(tierId, variant) => choose(active.office_id, tierId, variant)}
        />
      </div>

      <aside className="moc-side">
        <CartSummary lines={lines} totals={totals} covered={covered} />
        <div className="moc-pay">
          {plans.has_saved_card ? (
            <Button
              block
              type="primary"
              icon={<CreditCardOutlined />}
              disabled={!lines.length || preview}
              loading={submitting && pendingPayWith === "saved_card"}
              onClick={() => { setPendingPayWith("saved_card"); start("saved_card"); }}
              style={{ background: PURPLE, borderColor: PURPLE, height: 44 }}
            >
              Pay ${money(totals.total)} with saved card
            </Button>
          ) : null}
          <Button
            block
            type={plans.has_saved_card ? "default" : "primary"}
            disabled={!lines.length || preview}
            loading={submitting && pendingPayWith === "new_card"}
            onClick={() => { setPendingPayWith("new_card"); start("new_card"); }}
            style={plans.has_saved_card ? { height: 44, marginTop: 8 }
              : preview ? { height: 44 } : { background: PURPLE, borderColor: PURPLE, height: 44 }}
          >
            {plans.has_saved_card ? "Use a different card" : "Continue to payment"}
          </Button>
          {preview ? (
            <div className="moc-hint">Preview only — the practice's owner or admin pays here.</div>
          ) : !lines.length ? (
            <div className="moc-hint">
              {needing.length > 1 ? "Choose a plan for at least one office." : "Choose a plan to continue."}
            </div>
          ) : null}
        </div>
        {onCancel ? (
          <div className="moc-cancel"><span onClick={onCancel}>{cancelText}</span></div>
        ) : null}
      </aside>
    </div>
  );
}

const STATE_LABEL = {
  blocked: "Needs a plan",
  trial: "On a free trial",
  open: "No plan yet",
};

const STATE_TEXT = {
  active: "Active",
  declined: "Declined",
  provider_error: "Couldn't charge",
  needs_review: "Being checked",
  timeout: "Confirming",
};

function CartSummary({ lines, totals, covered = [], compact = false }) {
  const discount = Number(totals.discount) > 0;
  return (
    <div className={`moc-summary${compact ? " is-compact" : ""}`}>
      <div className="moc-summary-title">Order summary</div>
      {lines.length ? (
        lines.map((l) => (
          <div key={l.officeId} className="moc-line">
            <div className="moc-line-main">
              <span className="moc-line-office">{l.officeName}</span>
              <span className="moc-line-amount">${money(l.listSubtotal)}</span>
            </div>
            <div className="moc-line-sub">
              {l.title}{l.isAnnual ? " · 12 months, charged today" : " · billed monthly"}
            </div>
            {Number(l.discount) > 0 ? (
              <div className="moc-line-discount">
                <span>{l.offerLabel || "Discount"}</span>
                <span>−${money(l.discount)}</span>
              </div>
            ) : null}
          </div>
        ))
      ) : (
        <div className="moc-line-empty">No plans chosen yet.</div>
      )}
      {covered.length && !compact ? (
        <div className="moc-covered">
          {covered.map((o) => (
            <div key={o.office_id}>
              <CheckCircleFilled style={{ color: "#2e7d32" }} /> {o.office_name}
              <span> — {o.plan && o.plan.tier ? o.plan.tier.title : "on a plan"}</span>
            </div>
          ))}
        </div>
      ) : null}
      {lines.length ? (
        <div className="moc-totals">
          {discount ? (
            <div><span>Discounts</span><span>−${money(totals.discount)}</span></div>
          ) : null}
          <div><span>Subtotal</span><span>${money(totals.subtotal)}</span></div>
          {totals.taxLines.map((t) => (
            <div key={`${t.label}|${t.percent}`}>
              <span>{taxLineLabel(t, totals.taxLines.length)}</span><span>${money(t.amount)}</span>
            </div>
          ))}
          <div className="moc-total"><span>Total due today</span><span>${money(totals.total)}</span></div>
        </div>
      ) : null}
    </div>
  );
}

function TotalNote({ totals, count }) {
  return (
    <div className="co-charge">
      <div>
        {count > 1
          ? `$${money(totals.total)} will be charged today — one charge per office, each with its own receipt.`
          : `$${money(totals.total)} will be charged today.`}
      </div>
      <div className="co-note">
        Plans renew automatically until cancelled.{totals.taxLines.length ? " Taxes included." : ""}
      </div>
    </div>
  );
}
